/**
 * Catalog-derived selectors — the storefront's `simplifiedCatalog` module
 * (original module **74833** in `recovery-source/app-formatted.js`).
 *
 * Symbol map (original → here):
 *   `O` (exported as `lm`) → `selectPopularCatalogItems`
 *   `b`                    → `selectCatalogItemsMap`
 *
 * `A`, the function behind `O`, walks every category's items, runs each through
 * `convertItemToWorkableItem(item, platform, tenant_id)` — the platform /
 * tenant / sold-out filter — keeps the ones that survive AND carry
 * `is_featured`, then sorts by `featured_sort_id ?? 0` ascending.
 *
 * Here the filter half is already done: `GET /web/menu/:tenantId` applies the
 * tenant's platform restrictions and resolves the per-tenant `sold_out` array
 * server-side, so `state.menu.items` IS the workable catalog. Only the
 * `featured` test and the sort remain, and both are verbatim.
 */

import { createSelector } from "@reduxjs/toolkit";

import type { MenuItem } from "@/types";
import type { RootState } from "..";

const selectMenuItems = (state: RootState) => state.menu.items;

/**
 * `b` — every catalog item by id. `nq` (Order Again) looks each remembered
 * order line up in this map and drops any line whose item has since left the
 * catalog, which is what stops a delisted item from being offered for reorder.
 */
export const selectCatalogItemsMap = createSelector(
  [selectMenuItems],
  (items): Record<string, MenuItem> => {
    const map: Record<string, MenuItem> = {};
    for (const item of items) map[item._id] = item;
    return map;
  },
);

/** `lm` / `O` — the "Popular Items" rail's whole data source. */
export const selectPopularCatalogItems = createSelector(
  [selectMenuItems],
  (items): MenuItem[] =>
    items
      .filter((item) => Boolean(item.featured))
      .slice()
      .sort((a, b) => (a.featured_sort_id ?? 0) - (b.featured_sort_id ?? 0)),
);
